import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Stack, Typography, IconButton, Tabs, Tab,
  Skeleton, useTheme, alpha,
} from '@mui/material';
import { Close, MoreVert } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import { AdmissionCycle, AdmissionApplication } from '../../types/admissions';
import StatusChip from '../common/StatusChip';
import CandidateDetailsTab from './drawer/CandidateDetailsTab';
import CandidateTimelineTab from './drawer/CandidateTimelineTab';
import dateTimeUtils from '../../utils/dateTimeUtils';
import { fmtClassName } from '../../utils/pdf/format';
import CandidateFamilyTab from './drawer/CandidateFamilyTab';
import CandidateDocumentsTab from './drawer/CandidateDocumentsTab';
import CandidateFeesTab from './drawer/CandidateFeesTab';
import CandidateAssessmentTab from './drawer/CandidateAssessmentTab';
import CandidateDecisionTab from './drawer/CandidateDecisionTab';
import ApplicationActionsMenu from './ApplicationActionsMenu';
import { hasAnyScoring, planFor, cycleHasScoring } from '../../utils/admissionAssessment';
import { grNumberOf, classNameOf, APPLICATION_SOURCE_LABEL } from '../../utils/admissionRecords';
import PersonPhoto from '../people/PersonPhoto';
import { photoFilenameOf } from '../../utils/personPhoto';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useApplicationSiblings } from '../../hooks/useApplicationSiblings';
import SiblingsChip from './drawer/SiblingsChip';
import RecordEmails from '../email/RecordEmails';
import { NO_EMAIL_HINT } from './decisionEmails';
import { useSubscription } from '../../context/SubscriptionContext';

/**
 * CandidateDrawer — right-side detail for one application, so the list
 * stays scannable behind it. Status actions live in the header ⋮ menu;
 * stage work (documents, fees, marks, decision) happens on its own tab.
 */

type DrawerTab = 'details' | 'timeline' | 'family' | 'documents' | 'fees' | 'assessment' | 'decision' | 'emails';

const TAB_DEFS: Array<{ key: DrawerTab; label: string }> = [
  { key: 'details',     label: 'Details' },
  { key: 'timeline',    label: 'Timeline' },
  { key: 'family',      label: 'Family' },
  { key: 'documents',   label: 'Documents' },
  { key: 'fees',        label: 'Fees' },
  { key: 'assessment',  label: 'Assessment' },
  { key: 'decision',    label: 'Decision' },
  { key: 'emails',      label: 'Emails' },
];


interface Props {
  applicationId: string;
  cycle: AdmissionCycle;
  onClose: () => void;
  onMutated: () => void;   // Called whenever a mutation lands so the parent list can refresh.
}

const CandidateDrawer: React.FC<Props> = ({ applicationId, cycle, onClose, onMutated }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [app, setApp] = useState<AdmissionApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<DrawerTab>('details');

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { canWrite } = useModulePermissions();
  const { hasFeature } = useSubscription();
  const emailInPlan = hasFeature('email');
  const siblings = useApplicationSiblings(applicationId, app?.updatedAt);
  const showFamily = useCallback(() => setTab('family'), []);

  // The Assessment tab only exists when the applicant's pathway scores them; Emails only with Email in the plan.
  const tabs = useMemo(
    () => TAB_DEFS.filter(t => t.key === 'assessment'
      ? (app ? hasAnyScoring(planFor(cycle, app)) : cycleHasScoring(cycle))
      : t.key !== 'emails' || emailInPlan),
    [cycle, app, emailInPlan],
  );

  const load = async () => {
    setLoading(true);
    try {
      const data = await admissionApplicationService.get(applicationId);
      setApp(data);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load application', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const grNumber = useMemo(() => {
    if (!app) return '';
    return grNumberOf(app);
  }, [app]);

  const className = useMemo(() => {
    if (!app) return '';
    return fmtClassName(classNameOf(app));
  }, [app]);

  const handleAfterMutation = async () => {
    await load();
    onMutated();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open
        onClose={onClose}
        // Lift above the fixed AppBar (zIndex.drawer + 1 = 1201). Without
        // this, the drawer paper is clipped by the topbar and the header
        // disappears behind it. Matches the pattern used by
        // StudentProfileDrawer.
        ModalProps={{ keepMounted: false }}
        slotProps={{
          root: { style: { zIndex: 1250 } },
          paper: {
            style: { zIndex: 1250 },
            sx: {
              width: { xs: '100%', sm: 600, md: 720 },
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* ── Header ───────────────────────────────────────────────── */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          {loading || !app ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="circular" width={52} height={52} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width={180} height={28} />
                <Skeleton width={240} height={18} />
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <PersonPhoto
                name={app.name}
                caption={app.applicationNumber}
                photoFilename={photoFilenameOf(app)}
                size={52}
                owner={canWrite('admissions') ? { application: app._id } : undefined}
                onChanged={(photoFilename) => {
                  setApp(prev => (prev && typeof prev.personId === 'object'
                    ? { ...prev, personId: { ...prev.personId, photoFilename: photoFilename ?? undefined } }
                    : prev));
                  onMutated();
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>{app.name}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  <StatusChip status={{ kind: 'application', value: app.status }} />
                  {className && <Typography variant="body2" color="text.secondary">{className}</Typography>}
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>· {app.applicationNumber}</Typography>
                  {grNumber && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>· GR {grNumber}</Typography>
                  )}
                  {siblings && <SiblingsChip data={siblings} onClick={showFamily} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Applied {dateTimeUtils.formatDateForDisplay(app.createdAt)}
                  {APPLICATION_SOURCE_LABEL[app.source] ? `  ·  ${APPLICATION_SOURCE_LABEL[app.source]}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.25} sx={{ mt: -0.5, mr: -1 }}>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Application actions">
                  <MoreVert fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Box>

        {/* ── Tabs ─────────────────────────────────────────────────── */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as DrawerTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            minHeight: 44,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 44, minWidth: 0, px: 1.5, fontSize: '0.875rem' },
            '& .MuiTab-root.Mui-selected': { fontWeight: 600 },
          }}
        >
          {tabs.map(def => <Tab key={def.key} value={def.key} label={def.label} />)}
        </Tabs>

        {/* ── Tab panels ───────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: alpha(theme.palette.text.primary, 0.015) }}>
          {loading || !app ? (
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="rectangular" height={60} />
            </Stack>
          ) : (
            <>
              {tab === 'details'    && (
                <CandidateDetailsTab
                  app={app}
                  cycle={cycle}
                  canEdit={canWrite('admissions') && app.status !== 'Enrolled'}
                  onChanged={handleAfterMutation}
                />
              )}
              {tab === 'timeline'   && <CandidateTimelineTab app={app} cycle={cycle} />}
              {tab === 'family'     && <CandidateFamilyTab app={app} siblings={siblings} onChanged={handleAfterMutation} />}
              {tab === 'documents'  && <CandidateDocumentsTab app={app} cycle={cycle} onChanged={handleAfterMutation} />}
              {tab === 'fees'       && <CandidateFeesTab app={app} cycle={cycle} onChanged={handleAfterMutation} />}
              {tab === 'assessment' && <CandidateAssessmentTab app={app} cycle={cycle} onChanged={handleAfterMutation} />}
              {tab === 'decision'   && <CandidateDecisionTab app={app} cycle={cycle} onChanged={handleAfterMutation} />}
              {tab === 'emails'     && <RecordEmails entityType="admission_application" entityId={app._id} canResend={canWrite('admissions')} missingAddress={NO_EMAIL_HINT} />}
            </>
          )}
        </Box>
      </Drawer>

      <ApplicationActionsMenu
        app={app}
        cycle={cycle}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onMutated={handleAfterMutation}
      />
    </>
  );
};

export default CandidateDrawer;
